const state = {
  // 当前编辑的配置
  currentConfig: {
    problemId: "",
    problemTitle: "",
    initialStepId: "",
    steps: {}
  },
  // 当前编辑的步骤ID
  editingStepId: null,
  // 所有步骤数据
  steps: {},
  // 枚举数据
  enums: {
    problemStatus: [],
    commonTaskType: [],
    specTaskType: [],
    dealResult: [],
    conditionType: [],
    actionType: []
  }
};

const mutations = {
  // 设置当前配置
  SET_CURRENT_CONFIG(state, config) {
    state.currentConfig = { ...config };
    state.steps = { ...(config.steps || {}) };
  },
  // 设置步骤数据
  SET_STEPS(state, steps) {
    state.steps = { ...steps };
  },
  // 设置当前编辑的步骤ID
  SET_EDITING_STEP_ID(state, stepId) {
    state.editingStepId = stepId;
  },
  // 更新步骤
  UPDATE_STEP(state, { stepId, stepData }) {
    // 将空字符串字段转换为 null
    const cleanedStepData = { ...stepData };
    Object.keys(cleanedStepData).forEach((key) => {
      if (cleanedStepData[key] === "") {
        cleanedStepData[key] = null;
      }
    });

    state.steps = {
      ...state.steps,
      [stepId]: {
        ...state.steps[stepId],
        ...cleanedStepData
      }
    };
  },
  // 更新步骤的某个字段（支持嵌套字段，如 flowChartConfig.x）
  UPDATE_STEP_FIELD(state, { stepId, field, value }) {
    if (!state.steps[stepId]) {
      state.steps[stepId] = {};
    }

    // 如果值是空字符串，统一设置为null
    const normalizedValue = value === "" ? null : value;

    // 支持嵌套字段更新（如 flowChartConfig.x）
    if (field.includes(".")) {
      const fields = field.split(".");
      const currentStep = { ...state.steps[stepId] };
      let target = currentStep;

      // 遍历到倒数第二层
      for (let i = 0; i < fields.length - 1; i++) {
        const key = fields[i];
        if (!target[key] || typeof target[key] !== "object") {
          target[key] = {};
        } else {
          target[key] = { ...target[key] };
        }
        target = target[key];
      }

      // 设置最后一级的值
      target[fields[fields.length - 1]] = normalizedValue;

      state.steps = {
        ...state.steps,
        [stepId]: currentStep
      };
    } else {
      // 普通字段更新
      state.steps = {
        ...state.steps,
        [stepId]: {
          ...state.steps[stepId],
          [field]: normalizedValue
        }
      };
    }
  },
  // 添加步骤
  ADD_STEP(state, { stepId, stepData }) {
    // 将空字符串字段转换为 null
    const cleanedStepData = { ...stepData };
    Object.keys(cleanedStepData).forEach((key) => {
      if (cleanedStepData[key] === "") {
        cleanedStepData[key] = null;
      }
    });

    state.steps = {
      ...state.steps,
      [stepId]: { ...cleanedStepData }
    };
  },
  // 删除步骤
  REMOVE_STEP(state, stepId) {
    const newSteps = { ...state.steps };
    delete newSteps[stepId];
    state.steps = newSteps;
  },
  // 重置状态
  RESET_STATE(state) {
    state.currentConfig = {
      problemId: "",
      problemTitle: "",
      initialStepId: "",
      steps: {}
    };
    state.steps = {};
    state.editingStepId = null;
  },
  // 设置枚举数据
  SET_ENUMS(state, enums) {
    state.enums = { ...enums };
  }
};

const getters = {
  // 获取当前配置
  currentConfig: (state) => state.currentConfig,
  // 获取所有步骤
  steps: (state) => state.steps,
  // 获取当前编辑的步骤
  editingStep: (state) => {
    if (!state.editingStepId) return null;
    return state.steps[state.editingStepId] || null;
  },
  // 获取步骤ID列表
  stepIds: (state) => Object.keys(state.steps),
  // 获取完整的配置（包含步骤）
  fullConfig: (state) => ({
    ...state.currentConfig,
    steps: state.steps
  }),
  // 获取枚举数据
  enums: (state) => state.enums,
  // 获取问题状态枚举
  problemStatusOptions: (state) => state.enums.problemStatus || [],
  // 获取通用任务类型枚举
  commonTaskTypeOptions: (state) => state.enums.commonTaskType || [],
  // 获取特殊任务类型枚举
  specTaskTypeOptions: (state) => state.enums.specTaskType || [],
  // 获取处理结果枚举
  msgRoleOptions: (state) => state.enums.msgRole || [],
  // 获取条件类型枚举
  conditionTypeOptions: (state) => state.enums.conditionType || [],
  // 获取动作类型枚举
  actionTypeOptions: (state) => state.enums.actionType || [],
  // 获取特定问题枚举
  specProblemOptions: (state) => state.enums.specProblem || [],
  // 获取步骤选项列表（包含stepName的显示格式）
  stepOptions: (state) => {
    return Object.keys(state.steps).map((stepId) => {
      const step = state.steps[stepId];
      const stepName = step?.stepName || "";
      return {
        stepId,
        label: stepName ? `${stepName}` : stepId
      };
    });
  }
};

const actions = {
  // 初始化配置
  initConfig({ commit }, config) {
    commit("SET_CURRENT_CONFIG", config);
  },
  // 更新步骤
  updateStep({ commit }, { stepId, stepData }) {
    commit("UPDATE_STEP", { stepId, stepData });
  },
  // 更新步骤字段
  updateStepField({ commit }, { stepId, field, value }) {
    commit("UPDATE_STEP_FIELD", { stepId, field, value });
  },
  // 添加步骤
  addStep({ commit }, { stepId, stepData }) {
    commit("ADD_STEP", { stepId, stepData });
  },
  // 删除步骤
  removeStep({ commit }, stepId) {
    commit("REMOVE_STEP", stepId);
  },
  // 设置编辑步骤
  setEditingStep({ commit }, stepId) {
    commit("SET_EDITING_STEP_ID", stepId);
  },
  // 设置步骤数据
  setSteps({ commit }, steps) {
    commit("SET_STEPS", steps);
  },
  // 设置枚举数据
  setEnums({ commit }, enums) {
    commit("SET_ENUMS", enums);
  },
  // 重置状态
  resetState({ commit }) {
    commit("RESET_STATE");
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
